import Modal from './siteManagement.modal.component';
import siteGridOptions from './siteGrid.value';
import SiteMap from './siteMap';
import template from './siteManagement.component.html';

/**
 * @typedef {object} Coordinate
 * @property {string|number} [longitude]
 * @property {string|number} [latitude]
 */

/**
 * @typedef {object} Site
 * @property {string} name
 * @property {Coordinate[]} [border]
 * @property {string} [region]
 * @property {string[]} groups
 * @property {object[]|string[]} equipment
 * @property {object[]|string[]} rcus
 */

/**
 * Patch for digest issues caused by use of class getter
 *
 * @type {Array<Site>}
 * @todo Remove once migrated to SailsService/etc (wherein this is already fixed)
 */
let sitesGetCache;

class sitesManagement {
  constructor(
    $state,
    $stateParams,
    $transitions,
    growl,
    EquipmentInfoService,
    MapService,
    ModalService,
    RcuInfoService,
    SiteService,
  ) {
    'ngInject';

    this.service = {
      Site: SiteService,
      Equipment: EquipmentInfoService,
      RCU: RcuInfoService,
      Map: MapService,
      Modal: ModalService
    };

    this.growl = growl;
    this.$state = $state;
    this.$stateParams = $stateParams;


    /**
     * Private model of sites
     *
     * @constant
     * @type {Map}
     */
    this._sites_ = new Map();

    /**
     * GMaps Map instance
     *
     * @constant
     * @type {SiteMap}
     */
    this.sitePageMap = new SiteMap(this.service.Map, null, false);

    // Nested route handling
    this.transitionHooks = [
      $transitions.onSuccess({ to: 'admin.sites.site' }, () => {
        this.selected = this.$stateParams.id;
        this.fetchSiteNodes();
      }),
      $transitions.onSuccess({ to: 'admin.sites.new' }, () => this.newSite()),
      $transitions.onSuccess({ to: 'admin.sites.edit' }, () => this.editSite()),
    ];

    // Options object passed into the ui-grid directive
    this.gridOptions = siteGridOptions;
    this.gridOptions.onRegisterApi = (gridApi) => {
      this.gridApi = gridApi;
    };
  }

  $onInit() {
    // Grab sites resolved by the router
    this.resolvedSites.forEach(site => this.addSite(site));

    // If route has an id, set it as selected
    this._selected_ = this.$stateParams.id;

    switch (this.$state.current.name) {
      case 'admin.sites.site':
        this.fetchSiteNodes();
        break;
      case 'admin.sites.new':
        this.newSite();
        break;
      case 'admin.sites.edit':
        this.editSite();
        break;
      default:
    }
  }


  /**
   * @readonly
   * @returns {Site[]} A list all sites
   */
  get sites() {
    const storeValues = [...this._sites_.values()].sort((a, b) => a.name.localeCompare(b.name));
    if (!_.isEqual(sitesGetCache, storeValues)) {
      sitesGetCache = storeValues;
    }
    return sitesGetCache;
  }

  /**
   * DON'T ASSIGN THIS; use openSite modal instead
   *
   * @private
   * @param {string} id ID of site to select
   */
  set selected(id) {
    this._selected_ = id;
  }

  /**
   * @returns {Site|undefined} The currently selected Site, if any
   */
  get selected() {
    return this._sites_.get(this._selected_);
  }

  /**
   * Navigate to a given site
   *
   * @param {string} [id] Site to open; defaults to selected site
   * @returns {undefined}
   */
  openSite(id = this._selected_) {
    this.$state.go('admin.sites.site', { id });
  }

  /**
   * Navigate to the main sites page
   */
  openSites() {
    this.$state.go('admin.sites');
  }

  /**
   * Populates site node IDs with literal nodes &
   * updates the table accordingly
   *
   * @param {Site} [site] Site to fetch nodes for, defaults to selected
   * @returns {undefined}
   */
  async fetchSiteNodes({ equipment = [], rcus = [] } = this.selected) {
    try {
      const nodes = [
        ...await this.service.Equipment.getFullEquipment(equipment),
        ...await this.service.RCU.filterRcusById(rcus)
      ];
      this.updateGridData(nodes);
      this.setupMap('site-location', nodes);
    } catch {
      this.growl.error('Unable to retrieve site nodes. Try again');
    }
  }

  updateGridData(data) {
    this.gridOptions.data = data;
    this.gridApi.core.refreshRows();
  }

  /**
   * Adds a site to the view modal
   * Don't call this; use the modal instead
   *
   * @private
   * @param {Site} site Site to add
   * @returns {Site} Reference to new Site
   */
  addSite(site) {
    this._sites_.set(site.id, site);
    return site;
  }

  /**
   * Opens the modal for editing a site and submits changes
   *
   * @param {string} [id] Target - If omited, targets selected site
   * @returns {Promise<undefined>} Result of modal
   */
  editSite(id) {
    if (!id) {
      try {
        // Default to selected site ID, if any
        ({ id } = this.selected);
      } catch (err) {
        // This should never happen
        console.error('tried to edit without specifying site', err);
      }
    }
    return this
      .openModal(id)
      .then(async (newSite) => {
        try {
          this.assignEquipmentToSite(newSite.equipment, id);
          await this.service.Site.update(id, newSite);
          this.fetchSiteNodes();
          this.growl.success(`Updated ${newSite.name}`);
        } catch (err) {
          this.growl.error(err.error || err, { title: `Error updating ${newSite.name}` });
        }
      })
      .catch(_.noop) // User closed modal
      .finally(() => this.openSite(id));
  }

  /**
   * Opens the modal for creating a new site and submits it
   *
   * @returns {Promise<undefined>} Result of opening modal
   */
  newSite() {
    return this
      .openModal()
      .then(async (newSite) => {
        try {
          const site = await this.service.Site.create(newSite);
          const { equipment, rcus, groups } = newSite;
          this.addSite({ ...site, equipment, rcus, groups });
          this.assignEquipmentToSite(equipment, site.id);

          this.openSite(site.id);
          this.growl.success(site.name, { title: 'Created site' });
        } catch (err) {
          this.growl.error(err.error || err, { title: `Unable to create ${newSite.name}` });
        }
      })
      .catch(() => this.selected ? this.openSite() : this.$state.go('admin.sites'));
  }

  /**
   * Opens the modal for deleting a site and submits its request
   *
   * @param {string} [id] ID of site. Defaults to selected site, if omitted
   * @returns {Promise<undefined>} Result of opening confirmation modal
   * @throws If target site is not specified
   */
  destroySite(id = this.selected.id) {
    if (!id) {
      throw new TypeError('Tried to destroy without specifying or selecting target');
    }
    const { name: siteName, equipment } = this._sites_.get(id);
    return this.service.Modal
      .open({
        component: 'confirmationModal',
        resolve: {
          modalOptions: {
            modalTitle: 'Delete Site',
            optionalWarning: 'This will delete the site from the platform.',
            selectionCriteria: `The site ${siteName} will be removed.`,
            buttonText: 'Confirm',
          }
        }
      })
      .catch(/* User closed modal */)
      .then(() => {
        this.service.Site.destroy(id);
        this.assignEquipmentToSite(equipment, null);
      })
      .then(() => this._sites_.delete(id))
      .then(() => this.growl.success(`Deleted ${siteName}`))
      .then(() => this.$state.go('admin.sites'))
      .catch(err => this.growl.error(err.error || err, { title: `Unable to delete ${siteName}` }));
  }

  openModal(id) {
    return this.service.Modal.open({
      ...Modal,
      size: 'lg',
      resolve: {
        site: this._sites_.get(id) // ! Reference !
      }
    });
  }

  setupMap(htmlId, nodes) {
    // Setup initial map around texas with placeholder values.
    const mapElement = document.getElementById(htmlId);
    const mapOptions = { gestureHandling: 'greedy' };
    this.sitePageMap.drawMap(this.selected, nodes, mapElement, 'edit-site', mapOptions);
  }

  $onDestroy() {
    this.transitionHooks.forEach(deregister => deregister());
    this.sitePageMap.unlisten();
  }

  /**
   * This helper updates the *local* equipment model without refreshing all equipment.
   * ? Something similar for RCUS may eventually be needed. -Paul
   *
   * @param {string[]} equipment Equipment to assign to site
   * @param {string} site Site (mongo_id) to associate equipment with
   * @returns {undefined}
   */
  assignEquipmentToSite(equipment, site) {
    _.forEach(equipment, id => this.service.Equipment.updateEquipment({ id, site }));
  }

  handleSelect(item) {
    if (item?.id) {
      this.openSite(item.id);
    } else {
      this.selected = undefined;
      this.openSites();
    }
  }

}

export default {
  name: 'siteManagement',
  template,
  controller: sitesManagement,
  bindings: {
    resolvedSites: '<'
  }
};
